import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Search, X } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';

type Hit =
  | { type: 'ticket'; id: string; displayNumber: number; subject: string; organizationName: string; status: string; ticketType: string }
  | { type: 'org'; id: string; name: string; domain: string | null }
  | { type: 'contact'; id: string; email: string; name: string | null; organizationId: string; organizationName: string }
  | { type: 'kb'; id: string; slug: string; title: string; audience: string };

export function GlobalSearch(): React.ReactElement | null {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const cmdK = (isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (cmdK) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQ('');
      setHits(null);
      setActiveIdx(0);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setHits(null);
      return;
    }
    let cancelled = false;
    setBusy(true);
    const id = setTimeout(() => {
      apiFetch<{ hits: Hit[] }>(`/api/search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => {
          if (!cancelled) {
            setHits(r.hits);
            setActiveIdx(0);
          }
        })
        .catch((e) => {
          if (!cancelled) {
            setHits([]);
            console.warn('search failed', e instanceof ApiError ? e.message : e);
          }
        })
        .finally(() => {
          if (!cancelled) setBusy(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [q]);

  if (!open) return null;

  const hrefFor = (h: Hit): string => {
    switch (h.type) {
      case 'ticket': return `/tickets/${h.id}`;
      case 'org': return `/organizations/${h.id}`;
      case 'contact': return `/organizations/${h.organizationId}`;
      case 'kb': return `/kb/${h.slug}`;
    }
  };

  function go(h: Hit) {
    navigate(hrefFor(h));
    setOpen(false);
  }

  function onKey(e: React.KeyboardEvent) {
    if (!hits) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(hits.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && hits[activeIdx]) {
      e.preventDefault();
      go(hits[activeIdx]!);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 px-4 pt-24"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-4 py-3">
          <Search className="h-5 w-5 text-[var(--color-muted)]" />
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search tickets, orgs, contacts, KB articles…"
            className="flex-1 bg-transparent text-base outline-none"
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-[var(--color-muted)] hover:text-[var(--color-body)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {!q.trim() ? (
            <div className="px-4 py-6 text-sm text-[var(--color-muted)]">
              Start typing — looks across tickets (subject + T-number), organizations, contacts (email
              or name), and KB articles. ↑↓ to navigate, Enter to open.
            </div>
          ) : busy && !hits ? (
            <div className="px-4 py-6 text-sm text-[var(--color-muted)]">Searching…</div>
          ) : hits === null || hits.length === 0 ? (
            <div className="px-4 py-6 text-sm text-[var(--color-muted)]">No matches.</div>
          ) : (
            <ul>
              {hits.map((h, i) => (
                <li key={`${h.type}-${'id' in h ? h.id : i}`}>
                  <button
                    type="button"
                    onClick={() => go(h)}
                    onMouseEnter={() => setActiveIdx(i)}
                    className={
                      'flex w-full items-center gap-3 px-4 py-2 text-left text-sm ' +
                      (i === activeIdx
                        ? 'bg-[var(--color-t3-blue)]/10'
                        : 'hover:bg-[var(--color-surface)]')
                    }
                  >
                    <span className="rounded bg-[var(--color-surface)] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
                      {h.type}
                    </span>
                    <span className="flex-1 truncate">
                      {h.type === 'ticket' && (
                        <>
                          <span className="font-mono text-xs text-[var(--color-muted)]">T-{h.displayNumber}</span>{' '}
                          <span>{h.subject}</span>
                          <span className="ml-2 text-xs text-[var(--color-muted)]">· {h.organizationName}</span>
                        </>
                      )}
                      {h.type === 'org' && (
                        <>
                          <span>{h.name}</span>
                          {h.domain && <span className="ml-2 text-xs text-[var(--color-muted)]">· {h.domain}</span>}
                        </>
                      )}
                      {h.type === 'contact' && (
                        <>
                          <span>{h.name ?? h.email}</span>
                          <span className="ml-2 text-xs text-[var(--color-muted)]">· {h.organizationName}</span>
                        </>
                      )}
                      {h.type === 'kb' && (
                        <>
                          <span>{h.title}</span>
                          <span className="ml-2 text-xs text-[var(--color-muted)]">· {h.audience}</span>
                        </>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border-t border-[var(--color-border)] px-4 py-2 text-[11px] text-[var(--color-muted)]">
          ⌘K / Ctrl+K to open · Esc to close · ↑↓ navigate · Enter open
        </div>
      </div>
    </div>
  );
}
